// src/lib/http/stepup.ts
// The 403 half of contract §6.10: stepup_required → prompt → POST /auth/stepup → retry once; mfa_enrolment_required → the security page.

import { logout } from "../../api/auth/authApi";
import { announceLogout, CREDENTIAL_STEPS, endSession, recoverFromUnauthorized } from "./refresh";
import { getAuthSession, isEnrolmentConfined, isRecoverySession, loadAuthSession } from "./session";
import type { StrongMethod } from "../../types/auth/mfa";

export const ENROLMENT_PATH = "/settings/security";
export const DEFAULT_STEPUP_MAX_AGE = 300;
export const RECOVERY_EXPIRED = "recovery_expired";
export const RECOVERY_EXPIRED_MESSAGE = "Your recovery window has expired, sign in again";

export interface StepUpRequest {
	methods: StrongMethod[];
	maxAge: number;
}

// Resolves true once the session has stepped up; false when the user dismissed the prompt.
export type StepUpPrompter = (request: StepUpRequest) => Promise<boolean>;

// The step-up and sign-in endpoints themselves never get a prompt, or a refused step-up would prompt for itself.
const NO_STEPUP_PATH = new RegExp(`^/auth/(?:${[...CREDENTIAL_STEPS, "stepup", "passkeys/stepup/options", "logout", "logout-all"].join("|")})$`);

export const isStepUpExempt = (path: string): boolean => NO_STEPUP_PATH.test(path);

export type AuthFailureAction = "stepup" | "enrol" | "retry" | "fail";

// The §6.10 answer to one same-origin 401/403, shared by the axios and fetch halves; "retry" means a refresh just succeeded.
export const decideAuthFailure = async (
	status: number,
	code: string | undefined,
	details: Record<string, unknown>,
	path: string,
	sentAt: number,
	retried: boolean
): Promise<AuthFailureAction> => {
	if (status === 403 && code === "stepup_required") return isStepUpExempt(path) ? "fail" : "stepup";
	// A reason (email_code_required) is an enrolment step's own answer, handled by the security page.
	if (status === 403 && code === "mfa_enrolment_required") return details.reason ? "fail" : "enrol";
	if (retried) return "fail";
	return (await recoverFromUnauthorized(status, code, path, sentAt)) ? "retry" : "fail";
};

const STRONG: ReadonlySet<string> = new Set(["totp", "passkey"]);

export const strongMethods = (values: unknown): StrongMethod[] =>
	Array.isArray(values) ? (values.filter((v) => typeof v === "string" && STRONG.has(v)) as StrongMethod[]) : [];

const maxAgeOf = (details: Record<string, unknown>): number => {
	const value = Number(details.max_age);
	return Number.isFinite(value) && value > 0 ? value : DEFAULT_STEPUP_MAX_AGE;
};

let prompter: StepUpPrompter | null = null;
let pendingStepUp: Promise<boolean> | null = null;

export const registerStepUpPrompter = (fn: StepUpPrompter): (() => void) => {
	prompter = fn;
	return () => {
		if (prompter === fn) prompter = null;
	};
};

// Only identity's own refusals carry details.methods; a gateway or SDK refusal falls back to GET /auth/session (contract §4.7).
const resolveMethods = async (details: Record<string, unknown>): Promise<StrongMethod[]> => {
	if (Array.isArray(details.methods)) return strongMethods(details.methods);
	const known = strongMethods(getAuthSession().info?.mfa?.methods);
	if (known.length > 0) return known;
	try {
		return strongMethods((await loadAuthSession())?.mfa?.methods);
	} catch {
		// Logged in loadAuthSession; no methods means the enrolment page below.
		return [];
	}
};

// A recovery session never steps up with the lost factor (S32), so its stepup_required means the 10-minute window lapsed.
export const isRecoveryConfined = (info = getAuthSession().info): boolean => isRecoverySession(info) && isEnrolmentConfined(info);

let endingRecovery: Promise<void> | null = null;

// Signs out like signOut(), keeping RECOVERY_EXPIRED as the reason the login page shows.
export const endExpiredRecovery = (): Promise<void> => {
	if (!endingRecovery) {
		endingRecovery = (async () => {
			try {
				await logout();
			} catch (error) {
				console.error("session.recovery_expired.logout_failed", { status: (error as { response?: { status?: number } })?.response?.status });
			}
			announceLogout();
			endSession(RECOVERY_EXPIRED, { broadcast: false });
		})().finally(() => {
			endingRecovery = null;
		});
	}
	return endingRecovery;
};

// Requests refused together share one prompt.
export const requestStepUp = (details: Record<string, unknown> = {}): Promise<boolean> => {
	if (isRecoveryConfined()) {
		void endExpiredRecovery();
		return Promise.resolve(false);
	}
	if (!pendingStepUp) {
		pendingStepUp = (async () => {
			const methods = await resolveMethods(details);
			if (methods.length === 0) {
				void requireEnrolment({});
				return false;
			}
			if (!prompter) {
				console.error("session.stepup.no_prompter");
				return false;
			}
			return prompter({ methods, maxAge: maxAgeOf(details) });
		})().finally(() => {
			pendingStepUp = null;
		});
	}
	return pendingStepUp;
};

// Seam for tests: jsdom's location cannot be spied on.
export const enrolmentNavigator = {
	go: (path: string): void => {
		window.location.assign(path);
	},
};

let routerNavigate: ((path: string) => void) | null = null;

export const registerEnrolmentNavigator = (fn: (path: string) => void): (() => void) => {
	routerNavigate = fn;
	return () => {
		if (routerNavigate === fn) routerNavigate = null;
	};
};

// Go to the enrolment page via the SPA router when one is registered (StepUpHost), else a full load. No /auth/session re-read:
// the caller already holds the confined session in memory, so reloading would discard it and race the just-set login cookie.
export const enterEnrolment = (path: string = ENROLMENT_PATH): void => {
	if (routerNavigate) routerNavigate(path);
	else enrolmentNavigator.go(path);
};

// Only a same-origin path is followed, whatever an upstream puts in details.enrol_url.
export const enrolmentPathFrom = (details: Record<string, unknown>): string => {
	const url = details.enrol_url;
	return typeof url === "string" && /^\/(?![/\\])/.test(url) && !/[\\\s]/.test(url) ? url : ENROLMENT_PATH;
};

let pendingEnrolment: Promise<void> | null = null;

// Re-reads the session first so the guards see the confinement too, then goes to the security page once.
export const requireEnrolment = (details: Record<string, unknown> = {}): Promise<void> => {
	if (!pendingEnrolment) {
		pendingEnrolment = (async () => {
			try {
				await loadAuthSession();
			} catch {
				// Logged in loadAuthSession; the page itself re-reads the session.
			}
			const path = enrolmentPathFrom(details);
			if (typeof window !== "undefined" && window.location.pathname === path) return;
			enterEnrolment(path);
		})().finally(() => {
			pendingEnrolment = null;
		});
	}
	return pendingEnrolment;
};
